/**
 * Extract Vimeo video ID and privacy hash from various URL formats
 */
export function extractVimeoId(url: string): string | null {
  if (!url) return null;

  // Handle various Vimeo URL formats:
  // - https://vimeo.com/123456789
  // - https://player.vimeo.com/video/123456789
  // - https://player.vimeo.com/video/123456789?h=abc123 (with privacy hash)
  // - https://vimeo.com/channels/staffpicks/123456789
  const patterns = [
    /vimeo\.com\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/,
    /vimeo\.com\/channels\/[^\/]+\/(\d+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Extract privacy hash from Vimeo player URL
 */
export function extractVimeoHash(url: string): string | null {
  if (!url) return null;

  const hashMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/);
  return hashMatch ? hashMatch[1] : null;
}

/**
 * Fetch thumbnail URL from Vimeo's oEmbed API
 * For private videos, attempts to use the full player URL with privacy hash
 */
export async function getVimeoThumbnail(url: string): Promise<string | null> {
  try {
    if (!url) {
      console.warn('Empty URL provided to getVimeoThumbnail');
      return null;
    }

    const vimeoId = extractVimeoId(url);
    const privacyHash = extractVimeoHash(url);

    // console.log(`Extracting thumbnail for URL: ${url}`);
    // console.log(`  -> Vimeo ID: ${vimeoId}, Privacy hash: ${privacyHash || 'none'}`);

    if (!vimeoId) {
      console.error('Could not extract Vimeo ID from URL:', url);
      return null;
    }

    // For private videos with privacy hash, use the full player URL
    let oembedVideoUrl = `https://vimeo.com/${vimeoId}`;
    if (privacyHash) {
      oembedVideoUrl = `https://player.vimeo.com/video/${vimeoId}?h=${privacyHash}`;
    }

    // Use Vimeo's oEmbed API to get video metadata including thumbnail
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(oembedVideoUrl)}`;
    // console.log(`Fetching from oEmbed: ${oembedUrl}`);

    const response = await fetch(oembedUrl);

    if (!response.ok) {
      console.error(`Failed to fetch Vimeo thumbnail for ID ${vimeoId}:`, response.status, response.statusText);
      const errorText = await response.text();
      console.error('Error response body:', errorText);

      // If it's a 404, the video might be private without proper hash
      if (response.status === 404) {
        console.warn(`Video ${vimeoId} appears to be private or unlisted. If this is a private video, make sure the URL includes the privacy hash parameter (h=...).`);
      }

      return null;
    }

    const data = await response.json();
    // console.log(`Successfully fetched thumbnail for ${vimeoId}:`, data.thumbnail_url);

    // Vimeo returns thumbnail_url in the oEmbed response
    return data.thumbnail_url || null;
  } catch (error) {
    console.error('Error fetching Vimeo thumbnail for URL:', url, error);
    return null;
  }
}

/**
 * Batch fetch thumbnails for multiple Vimeo URLs
 * Supports both string URLs and objects with a 'value' property
 */
export async function getVimeoThumbnails(urls: (string | { value: string })[]): Promise<(string | null)[]> {
  // console.log('Fetching thumbnails for URLs:', urls);

  return Promise.all(urls.map((url) => {
    // Handle both string URLs and objects with a 'value' property
    const urlString = typeof url === 'string' ? url : url?.value;
    return getVimeoThumbnail(urlString);
  }));
}
